import { useMemo } from 'react'
import Debug from 'debug'
import { sleep } from 'radash'
import type { Interceptor, Transport } from '@connectrpc/connect'
import { Code, ConnectError } from '@connectrpc/connect'
import { createGrpcWebTransport } from '@connectrpc/connect-web'

import {
  forceRefreshAccessToken,
  getOrRefreshAccessToken,
} from '@jotta/auth-client/useAuthStatus'
import { env } from '@jotta/utils/env'
import { enrichError } from '@jotta/utils/error'
import { readVersion } from '@jotta/utils/frontendbackend'
import { resolveVisitorId } from '@jotta/utils/matomo'
import { nextXid } from '@jotta/utils/xid'

const debug = Debug('grpc:transport')

let userAgent: string | undefined

function getUserAgent() {
  userAgent ??= `web/${readVersion() ?? `unknown-${env.env}`} ${navigator.userAgent}`
  return userAgent
}

declare global {
  interface Window {
    __CONNECT_WEB_DEVTOOLS__: Interceptor | undefined
  }
}

export const devToolsInterceptor: Interceptor = next => {
  const connectwebdevtools = window.__CONNECT_WEB_DEVTOOLS__
  return connectwebdevtools ? connectwebdevtools(next) : req => next(req)
}

export const logInterceptor: (name: string, xid?: string) => Interceptor =
  (transportName, anXid) => next => async req => {
    const xid = anXid || nextXid()
    const methodName = req.method.name
    const log = debug.extend(transportName).extend(methodName).extend(xid)
    try {
      const signal = req.signal
      signal.addEventListener('abort', () =>
        // the built-in timeout signaling produces just a basic "the operation timed out" error message
        // for streaming operations. Let's add some more context to the error message.
        enrichError(signal.reason, xid, methodName, transportName),
      )
      req.header.set('x-id', xid)
      req.header.set('x-user-agent', getUserAgent())
      log('Request', req)
      const res = await next(req)
      log('Response', res)
      return res
    } catch (error) {
      enrichError(error, xid, methodName, transportName)
      if (error instanceof ConnectError) {
        const { code } = error
        const codeName = Code[code]
        log('%s - code "%s" message "%s"', error.name, codeName, error.message)
      } else if (error instanceof Error) {
        log('%s: message: %s', error.name, error.message, error)
      } else {
        log('Unknown error', error)
      }
      throw error
    }
  }

/** Sets the access token on a call's own headers. */
function withToken(header: HeadersInit | undefined, token: string) {
  const result = new Headers(header)
  if (token) {
    result.set('Authorization', `Bearer ${token}`)
  }
  return result
}

/**
 * Sends the access token. When `tokenRefresher` is given and the server
 * rejects a call as unauthenticated, it gets a new token and makes the call
 * once more: the timed refresh can run late (a sleeping laptop, a throttled
 * background tab), and a stale token must not end on the error page.
 * `tokenRefresher` gets the rejected token, so a refresher can hand out a
 * token that already replaced it instead of refreshing again. It must return
 * a token for the same user as the rejected one, or ''.
 *
 * The retry wraps the transport rather than living in an interceptor, so it
 * is a new call: it gets its own deadline and `grpc-timeout` header, a new
 * x-id and a fresh interceptor chain. Time spent getting a token doesn't use
 * up the call's deadline. Only the caller's signal is shared, so cancelling
 * the call also cancels the retry.
 *
 * Server streams are retried too (the photo lists): their request messages
 * are buffered so the retry can send them again, and a rejection on the first
 * read of the response stream is retried as long as nothing has been
 * received. After a retry, `header` and `trailer` describe the first call.
 */
export function withAuthRetry(
  inner: Transport,
  tokenResolver: () => Promise<string>,
  tokenRefresher?: (rejectedToken: string) => Promise<string>,
): Transport {
  const resolveToken = async () => {
    try {
      return await tokenResolver()
    } catch (error) {
      // Same shape as a failure inside the call, so retries classify it alike.
      // A token lookup that timed out is a timeout, not a cancellation.
      if (error instanceof Error && error.name === 'TimeoutError') {
        throw new ConnectError(error.message, Code.DeadlineExceeded)
      }
      throw ConnectError.from(error)
    }
  }

  /** A new token for a retry, or undefined when retrying cannot help. */
  const freshToken = async (
    error: unknown,
    token: string,
    methodName: string,
    signal: AbortSignal | undefined,
  ) => {
    const log = debug.extend(methodName)
    const unauthenticated =
      error instanceof ConnectError && error.code === Code.Unauthenticated
    if (!unauthenticated || !tokenRefresher || !token) {
      return undefined
    }
    const fresh = await tokenRefresher(token).catch(() => '')
    if (!fresh || fresh === token || signal?.aborted) {
      return undefined
    }
    log('Unauthenticated with a stale token: retrying with a new one')
    return fresh
  }

  return {
    async unary(method, signal, timeoutMs, header, input, contextValues) {
      const token = await resolveToken()
      if (!token) {
        debug.extend(method.name)(
          'Token not found, performing request without authorization',
        )
      }
      const call = (callToken: string) =>
        inner.unary(
          method,
          signal,
          timeoutMs,
          withToken(header, callToken),
          input,
          contextValues,
        )
      try {
        return await call(token)
      } catch (error) {
        const fresh = await freshToken(error, token, method.name, signal)
        if (!fresh) {
          throw error
        }
        return call(fresh)
      }
    },

    async stream(method, signal, timeoutMs, header, input, contextValues) {
      const token = await resolveToken()
      if (!tokenRefresher || !token) {
        return inner.stream(
          method,
          signal,
          timeoutMs,
          withToken(header, token),
          input,
          contextValues,
        )
      }
      // Buffer the request messages (one for a server stream) to send again.
      const messages: (typeof input extends AsyncIterable<infer M>
        ? M
        : never)[] = []
      for await (const message of input) {
        messages.push(message)
      }
      const call = (callToken: string) =>
        inner.stream(
          method,
          signal,
          timeoutMs,
          withToken(header, callToken),
          (async function* () {
            yield* messages
          })(),
          contextValues,
        )

      let first: Awaited<ReturnType<typeof call>>
      try {
        first = await call(token)
      } catch (error) {
        const fresh = await freshToken(error, token, method.name, signal)
        if (!fresh) {
          throw error
        }
        return call(fresh)
      }
      const message = (async function* () {
        let received = false
        try {
          for await (const message of first.message) {
            received = true
            yield message
          }
        } catch (error) {
          const fresh = received
            ? undefined
            : await freshToken(error, token, method.name, signal)
          if (!fresh) {
            throw error
          }
          yield* (await call(fresh)).message
        }
      })()
      return { ...first, message }
    },
  }
}

const trackingInterceptor: Interceptor = next => async req => {
  const visitorId = resolveVisitorId()
  if (visitorId) {
    req.header.set('x-vid', visitorId)
  }
  return next(req)
}

/**
 * Slow down requests
 *
 * Only use for testing loading states during development!
 */
export const slowInterceptor: Interceptor = next => async req => {
  await sleep(500)
  return next(req)
}

export const publicTransport = createGrpcWebTransport({
  baseUrl: env.grpcApi,
  defaultTimeoutMs: 10000,
  interceptors: [
    devToolsInterceptor,
    logInterceptor('publicTransport'),
    trackingInterceptor,
  ],
})
export const createAuthTransport = ({
  timeoutMs = 10_000,
  tokenResolver,
  tokenRefresher,
  baseUrl = env.grpcApi,
  xid,
}: {
  timeoutMs?: number
  /** A fixed token source; not refreshed on 401 unless `tokenRefresher` is given. */
  tokenResolver?: () => Promise<string>
  /**
   * Gets a new token when a call with `tokenResolver`'s token is rejected as
   * unauthenticated. Pass it only for the user's own access token, never
   * for share tokens.
   */
  tokenRefresher?: (rejectedToken: string) => Promise<string>
  baseUrl?: string
  xid?: string
}) =>
  withAuthRetry(
    createGrpcWebTransport({
      baseUrl,
      defaultTimeoutMs: timeoutMs,
      interceptors: [
        devToolsInterceptor,
        logInterceptor('authTransport', xid),
        trackingInterceptor,
      ],
    }),
    tokenResolver ?? getOrRefreshAccessToken,
    tokenResolver ? tokenRefresher : forceRefreshAccessToken,
  )
export const authTransport = createAuthTransport({})

export function useAuthTransport({
  timeoutMs,
  token,
}: {
  timeoutMs?: number
  token?: string
}) {
  return useMemo(
    () =>
      createAuthTransport({
        timeoutMs,
        tokenResolver: token ? async () => token : undefined,
      }),
    [timeoutMs, token],
  )
}
